 'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import ServiceCard from './ServiceCard';

// import ServiceComparison from './ServiceComparison';

import CostCalculator from './CostCalculator';
import ClientTestimonials from '@/app/homepage/components/ClientTestimonials';
import ServiceModal from './ServiceModal';
import CatalogsSection from './CatalogsSection';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import SERVICES from '../servicesData';


interface Service {
  id: string;
  title: string;
  description: string;
  detailedDescription: string;
  features: string[];
  image: string;
  alt: string;
  icon: string;
  priceRange: string;
  timeline: string;
  scope: string;
  process: string[];
  gallery: Array<{image: string;alt: string;}>;
}

interface Testimonial {
  id: string;
  name: string;
  location: string;
  service: string;
  rating: number;
  comment: string;
  image: string;
  alt: string;
  projectType: string;
}

interface ComparisonFeature {
  feature: string;
  residential: boolean;
  modular: boolean;
  luxury: boolean;
  renovation: boolean;
  turnkey: boolean;
  consultation: boolean;
}

const ServicesInteractive = () => {
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeCalculator, setActiveCalculator] = useState<string>('residential');

  // Create a local copy of SERVICES and deduplicate by `id` to avoid mutating the imported array
  const services: Service[] = Array.from(
    new Map((SERVICES as Service[]).map((s) => [s.id, s])).values()
  );

  // Limit calculator options to only residential, modular and renovation as requested
  const calculatorServices = services.filter((s) => ['residential', 'modular', 'renovation'].includes(s.id));


  const testimonials: Testimonial[] = [
  {
    id: '1',
    name: 'Priya Sharma',
    location: 'Bangalore',
    service: 'residential',
    rating: 5,
    comment: 'Hayat Interiors transformed our 3BHK apartment into a beautiful home that perfectly balances modern design with our traditional values. The well-planned layout and thoughtful space planning for our joint family exceeded all expectations. The team understood our need for a dedicated pooja room and created a stunning space that feels both sacred and contemporary.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_111e29bbe-1763294966477.png",
    alt: 'Professional Indian woman in her thirties with long black hair wearing traditional kurta',
    projectType: '3BHK Apartment'
  },
  {
    id: '2',
    name: 'Rajesh Kumar',
    location: 'Mumbai',
    service: 'modular',
    rating: 5,
    comment: 'The modular kitchen designed by Hayat Interiors is a dream come true for my wife. The specialized storage for Indian spices, pressure cookers, and extensive utensils is incredibly practical. They truly understand Indian cooking needs. The quality of materials and attention to detail in every drawer and cabinet is exceptional.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e97714b6-1763291772723.png",
    alt: 'Middle-aged Indian man with short black hair and beard wearing casual shirt',
    projectType: 'Modular Kitchen'
  },
  {
    id: '3',
    name: 'Ananya Reddy',
    location: 'Hyderabad',
    service: 'luxury',
    rating: 5,
    comment: 'The luxury styling service brought together the most exquisite handcrafted furniture and traditional textiles. Every piece tells a story of Indian craftsmanship. The Banarasi silk cushions, brass accent pieces, and custom-carved furniture create a space that feels both luxurious and authentically Indian. Worth every rupee invested.',
    image: "https://images.unsplash.com/flagged/photo-1565939627066-aff72b006535",
    alt: 'Young Indian woman with shoulder-length hair wearing elegant traditional jewelry',
    projectType: 'Luxury Villa Styling'
  },
  {
    id: '4',
    name: 'Vikram Malhotra',
    location: 'Delhi',
    service: 'renovation',
    rating: 5,
    comment: 'Our ancestral haveli renovation was handled with such care and respect for the original architecture. Hayat Interiors preserved the beautiful carved woodwork and traditional jali screens while modernizing the electrical and plumbing systems. The result is a home that honors our heritage while providing modern comfort for our family.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ed4c2fc5-1763294753675.png",
    alt: 'Senior Indian man with grey hair and mustache wearing traditional kurta pajama',
    projectType: 'Heritage Haveli'
  },
  {
    id: '5',
    name: 'Meera Iyer',
    location: 'Chennai',
    service: 'turnkey',
    rating: 5,
    comment: 'As an NRI, managing a home project from abroad seemed impossible until we found Hayat Interiors. Their turnkey service was flawless - weekly video updates, transparent communication, and impeccable quality control. We returned to a fully furnished, move-in ready home that exceeded our expectations. Highly recommend for NRI families.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a6cc7b76-1763300209811.png",
    alt: 'Professional Indian woman in her forties with tied-back hair wearing business attire',
    projectType: 'NRI Turnkey Project'
  },
  {
    id: '6',
    name: 'Arjun Patel',
    location: 'Ahmedabad',
    service: 'consultation',
    rating: 5,
    comment: 'The consultation service provided exactly the guidance we needed. The comprehensive design plans, material recommendations, and vendor list helped us execute our project confidently. The periodic site visits ensured quality, and the team was always available to answer questions. Great value for money and professional expertise.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_144804231-1763293919368.png",
    alt: 'Young Indian man with short hair and glasses wearing casual polo shirt',
    projectType: 'Design Consultation'
  }];


  const comparisonFeatures: ComparisonFeature[] = [
  { feature: 'Complete Space Planning', residential: true, modular: false, luxury: false, renovation: true, turnkey: true, consultation: true },
  { feature: 'Design Principles', residential: true, modular: false, luxury: false, renovation: true, turnkey: true, consultation: true },
  { feature: 'Custom Furniture Design', residential: true, modular: false, luxury: true, renovation: true, turnkey: true, consultation: false },
  { feature: 'Modular Kitchen/Wardrobes', residential: false, modular: true, luxury: false, renovation: false, turnkey: true, consultation: false },
  { feature: 'Luxury Styling & Curation', residential: false, modular: false, luxury: true, renovation: false, turnkey: true, consultation: false },
  { feature: 'Heritage Preservation', residential: false, modular: false, luxury: false, renovation: true, turnkey: false, consultation: false },
  { feature: 'Project Management', residential: true, modular: true, luxury: false, renovation: true, turnkey: true, consultation: false },
  { feature: 'Vendor Coordination', residential: true, modular: true, luxury: true, renovation: true, turnkey: true, consultation: false },
  { feature: 'Material Procurement', residential: true, modular: true, luxury: true, renovation: true, turnkey: true, consultation: false },
  { feature: 'Quality Control', residential: true, modular: true, luxury: true, renovation: true, turnkey: true, consultation: true },
  { feature: 'Post-Completion Support', residential: true, modular: true, luxury: true, renovation: true, turnkey: true, consultation: false },
  { feature: 'Remote Collaboration', residential: false, modular: false, luxury: false, renovation: false, turnkey: true, consultation: true }];


  const handleLearnMore = (serviceId: string) => {
    const service = services.find((s) => s.id === serviceId);
    if (service) {
      setSelectedService(service);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedService(null), 300);
  };

  // Carousel state for hero sliding images (use local images from public/assets)
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const slidingImages = [
    { src: '/assets/living-room.jpg', alt: 'Living room with traditional textiles' },
    { src: '/assets/Living.jpg', alt: 'Warm contemporary living area' },
    { src: '/assets/dining.jpg', alt: 'Stylish dining space' },
    { src: '/assets/Enscape_2024-11-12-15-20-07.jpg', alt: 'Interior perspective' }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slidingImages.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [isAutoPlaying]);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] overflow-hidden bg-black pt-20">
        <div className="absolute inset-0 top-20">
          {slidingImages.map((img, index) => (
            <div
              key={img.src}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
            >
              <AppImage src={img.src} alt={img.alt} fill className="object-cover scale-105" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            </div>
          ))}
        </div>

        <div className="relative min-h-[70vh] flex items-center py-12 md:py-16 lg:py-32">
          <div className="w-full px-4 md:px-8 lg:px-20 max-w-[1400px] mx-auto">
            <div className="max-w-4xl space-y-4 md:space-y-6">
              <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-white/10 backdrop-blur-xl border border-white/20">
                <div className="w-1.5 h-1.5 bg-white rounded-full" />
                <span className="text-xs font-medium text-white/90 tracking-widest uppercase">
                  What We Offer
                </span>
              </div>

              <h1 className="font-headline text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-normal leading-tight md:leading-[0.95] text-white tracking-tight">
                Our Services
              </h1>

              <p className="text-base md:text-lg lg:text-xl text-white/80 leading-relaxed max-w-2xl font-normal">
                Comprehensive interior design solutions that honor Indian heritage while embracing contemporary luxury
              </p>
            </div>
          </div>
        </div>
        {/* Slide Controls (left/right + dots) */}
        <button
          onClick={() => { setIsAutoPlaying(false); setCurrentSlide((prev) => (prev - 1 + slidingImages.length) % slidingImages.length); }}
          className="absolute left-8 top-[42vh] -translate-y-1/2 w-12 h-12 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full flex items-center justify-center hover:bg-white/10 transition-all duration-300"
          aria-label="Previous slide"
        >
          <Icon name="ChevronLeftIcon" size={18} className="text-white/80" />
        </button>

        <button
          onClick={() => { setIsAutoPlaying(false); setCurrentSlide((prev) => (prev + 1) % slidingImages.length); }}
          className="absolute right-8 top-[42vh] -translate-y-1/2 w-12 h-12 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full flex items-center justify-center hover:bg-white/10 transition-all duration-300"
          aria-label="Next slide"
        >
          <Icon name="ChevronRightIcon" size={18} className="text-white/80" />
        </button>

        <div className="absolute left-1/2 transform -translate-x-1/2 bottom-10 flex gap-2">
          {slidingImages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => { setIsAutoPlaying(false); setCurrentSlide(idx); }}
              className={`h-0.5 rounded-full transition-all duration-500 ${idx === currentSlide ? 'w-12 bg-white' : 'w-6 bg-white/30 hover:bg-white/50'}`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>

      </section>

      {/* Services Grid Section */}
      <section className="py-12 md:py-16 lg:py-24 px-4 md:px-8 lg:px-20 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-10 md:mb-12 lg:mb-16">
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-black/5 backdrop-blur-xl border border-black/10 mb-6">
              <div className="w-1.5 h-1.5 bg-black rounded-full" />
              <span className="text-xs font-medium text-black/90 tracking-widest uppercase">
                Explore Services
              </span>
            </div>
            <h2 className="font-headline text-3xl md:text-4xl lg:text-6xl font-normal text-black mb-6 tracking-tight">
              Explore Our Services
            </h2>
            <p className="text-base md:text-lg text-black/60 max-w-2xl mx-auto font-normal">
              From complete home transformations to specialized consultations, we offer tailored solutions for every Indian home
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
            {services.map((service) =>
            <ServiceCard
              key={service.id}
              service={service}
              onLearnMore={handleLearnMore} />

            )}
          </div>
        </div>
      </section>

      {/* Catalogs Section */}
      <CatalogsSection />

      {/* Service Comparison Section */}
      {/* <section className="py-12 md:py-16 lg:py-24 px-4 md:px-8 lg:px-20 bg-zinc-50">
        <div className="max-w-[1400px] mx-auto">
          <ServiceComparison features={comparisonFeatures} />
        </div>
      </section> */}

      {/* Cost Calculator Section */}
      <section className="py-12 md:py-16 lg:py-24 px-4 md:px-8 lg:px-20 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-10 md:mb-12 lg:mb-16">
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-black/5 backdrop-blur-xl border border-black/10 mb-6">
              <div className="w-1.5 h-1.5 bg-black rounded-full" />
              <span className="text-xs font-medium text-black/90 tracking-widest uppercase">
                Budget Planner
              </span>
            </div>
            <h2 className="font-headline text-3xl md:text-4xl lg:text-5xl font-normal text-black mb-6 tracking-tight">
              Estimate Your Project Cost
            </h2>
            <p className="text-base md:text-lg text-black/60 max-w-2xl mx-auto font-normal">
              Get an approximate cost estimate for your interior design project
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12">
            <div>
              <label className="block text-sm font-medium text-black mb-4 uppercase tracking-wider">
                Select Service Type
              </label>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {calculatorServices.map((service) =>
                <button
                  key={service.id}
                  onClick={() => setActiveCalculator(service.id)}
                  className={`px-4 py-4 rounded-xl text-sm font-medium transition-all duration-300 border ${
                  activeCalculator === service.id ?
                  'bg-black text-white border-black shadow-lg' :
                  'bg-white text-black border-zinc-200 hover:border-black/30'}`
                  }>

                    {service.title.split(' ')[0]}
                  </button>
                )}
              </div>
            </div>

            <CostCalculator serviceType={activeCalculator} />
          </div>
        </div>
      </section>

      {/* Testimonials Section (use homepage client testimonials) */}
      <section className="py-12 md:py-16 lg:py-24 px-4 md:px-8 lg:px-20 bg-zinc-50">
        <div className="max-w-[1400px] mx-auto">
          <ClientTestimonials />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 lg:py-24 px-4 md:px-8 lg:px-20 bg-black">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 mb-6">
            <div className="w-1.5 h-1.5 bg-white rounded-full" />
            <span className="text-xs font-medium text-white/90 tracking-widest uppercase">
              Get Started
            </span>
          </div>
          <h2 className="font-headline text-3xl md:text-4xl lg:text-6xl font-normal text-white mb-6 tracking-tight">
            Ready to Transform Your Home?
          </h2>
          <p className="text-base md:text-lg text-white/70 mb-8 md:mb-10 font-normal max-w-2xl mx-auto">
            Book a consultation with our design experts and start your journey to a beautiful, culturally-rooted home
          </p>
          <div className="flex justify-center">
            <Link href="/contact" className="px-10 py-5 bg-white text-black font-medium rounded-full transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-white/20 inline-block active:scale-95 min-h-[44px]">
              Book Free Consultation
            </Link>
          </div>
        </div>
      </section>

      <ServiceModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        service={selectedService} />

    </div>);

};

export default ServicesInteractive;